import { useEffect, useState } from 'react';
import {
  Alert, Button, CircularProgress, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { getTaxrisGateway, setTaxrisGateway } from './api';
import type { TaxrisGateway, Writable } from './api';
import { apiErrorMessage } from '@/lib/errors';
import { GlassDialog } from '@/components/GlassDialog';

const CHOICES: { value: Writable; label: string; hint: string }[] = [
  { value: 'default', label: 'ຕາມຄ່າລະບົບ', hint: 'ໃຊ້ຄ່າທີ່ຕັ້ງໄວ້ໃນເຊີບເວີ (USE_NEW_TAXRIS)' },
  { value: 'new', label: 'TaxRIS ໃໝ່', hint: 'eTax Invoice Gateway (HMAC)' },
  { value: 'legacy', label: 'TaxRIS ເກົ່າ', hint: 'endpoint ແບບ hash-key' },
];

/** The read-only states, each with the remedy that actually applies to it. */
const BLOCKED = {
  unavailable: {
    severity: 'info' as const,
    message: 'ບໍລິສັດນີ້ຍັງບໍ່ທັນອັບເດດຖານຂໍ້ມູນ — ຈະອັບເດດອັດຕະໂນມັດເມື່ອມີຄົນ login '
      + 'ເຂົ້າບໍລິສັດນີ້ຄັ້ງທຳອິດຫຼັງຈາກ deploy ແລ້ວຈຶ່ງຈະຕັ້ງຄ່ານີ້ໄດ້',
  },
  unprovisioned: {
    severity: 'warning' as const,
    message: 'ຖານຂໍ້ມູນຂອງບໍລິສັດນີ້ບໍ່ມີແຖວ setting — ການສ້າງບໍລິສັດອາດຂາດຕອນ. '
      + 'ການ login ຈະບໍ່ແກ້ໃຫ້ ຕ້ອງໃຫ້ຜູ້ດູແລລະບົບແກ້ດ້ວຍມື',
  },
};

// `Object.hasOwn`, not `in`: `in` walks the prototype chain, so a gateway of
// 'toString' or 'constructor' would read as blocked.
const readOnly = (g: TaxrisGateway): g is keyof typeof BLOCKED => Object.hasOwn(BLOCKED, g);

export function TaxrisGatewayDialog({
  companyId, companyName, open, onClose, basePath = '/api/companies',
}: {
  companyId: number | null;
  companyName?: string;
  open: boolean;
  onClose: () => void;
  basePath?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tagged with the company it was loaded for: the dialog is reused across rows,
  // so an untagged value would let the previous company's state (and the
  // operator's unsaved choice) survive into — and be PUT to — the next one.
  // `current` may be a read-only state (see BLOCKED) rather than a choice.
  const [loaded, setLoaded] = useState<
    { id: number; current: TaxrisGateway; choice: Writable } | null
  >(null);
  const view = loaded !== null && loaded.id === companyId ? loaded : null;

  useEffect(() => {
    const id = companyId;
    if (!open || id == null) return;
    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await getTaxrisGateway(id as number, basePath);
        if (!active) return;
        // A read-only tenant has nothing stored, so hold the neutral choice
        // rather than pre-selecting something nobody picked.
        const choice = readOnly(res.gateway) ? 'default' : res.gateway;
        setLoaded({ id: id as number, current: res.gateway, choice });
      } catch {
        if (active) {
          setError('ບໍ່ສາມາດໂຫຼດຄ່າໄດ້');
          // Nothing was read, so nothing may be shown or sent. Without this the
          // same company's previous opening — including an unsaved choice —
          // stays in state, and once `loading` clears it reads as loaded: radios
          // under the error banner and an enabled save for a value nobody read.
          setLoaded(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => { active = false; };
  }, [open, companyId, basePath]);

  const choice = view?.choice ?? 'default';
  const blocked = view !== null && readOnly(view.current) ? BLOCKED[view.current] : null;
  // `loading` matters: on a reopen the previous opening's unsaved choice is
  // still in state while the fresh value is being read, and saving it would
  // write a value the operator never confirmed against what is stored now.
  const dirty = view !== null && !loading && blocked === null && choice !== view.current;

  async function onSave() {
    if (companyId == null || !dirty) return;
    setSaving(true);
    try {
      await toast.promise(
        setTaxrisGateway(companyId, choice, basePath),
        { loading: 'ກຳລັງບັນທຶກ...', success: 'ບັນທຶກສຳເລັດ', error: (e) => apiErrorMessage(e, 'ບັນທຶກບໍ່ສຳເລັດ') },
      );
      setLoaded((l) => (l !== null && l.id === companyId ? { ...l, current: choice } : l));
      onClose();
    } catch {
      // toast.promise already displayed the error
    } finally {
      setSaving(false);
    }
  }

  return (
    <GlassDialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        ລະບົບ TaxRIS
        {companyName && (
          <Typography variant="body2" color="text.secondary">{companyName}</Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <CircularProgress size={28} sx={{ display: 'block', mx: 'auto', my: 3 }} />
        ) : view === null ? (
          // Nothing loaded for this company — the error Alert above says why.
          // Radios here would paint 'ຕາມຄ່າລະບົບ' as a stored value nobody read,
          // and clicking them cannot move the selection: there is no state to
          // update until a load succeeds.
          null
        ) : blocked !== null ? (
          <Alert severity={blocked.severity}>{blocked.message}</Alert>
        ) : (
          <FormControl>
            <RadioGroup
              value={choice}
              onChange={(e) => setLoaded((l) =>
                (l !== null && l.id === companyId ? { ...l, choice: e.target.value as Writable } : l))}
            >
              {CHOICES.map((c) => (
                <FormControlLabel
                  key={c.value}
                  value={c.value}
                  control={<Radio size="small" />}
                  label={
                    <Stack>
                      <Typography variant="body2">{c.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.hint}</Typography>
                    </Stack>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ປິດ</Button>
        <Button variant="contained" disabled={!dirty || saving} onClick={() => void onSave()}>ບັນທຶກ</Button>
      </DialogActions>
    </GlassDialog>
  );
}
